/* Whose data is on this device, and the one door that clears it when that
 * account leaves.
 *
 * Everything the app keeps locally — the Learn blob, the Ledger's prefs and
 * game caches, reminder prefs — is the source of truth for the RUNNING app,
 * and on sign-in it is merged INTO the account: max XP, union of game caches,
 * newest statement wins (learn/sync.js, OpeningLedger's hydrate,
 * notifyPrefs.js#mergePrefs). That merge is right for the device's own
 * history — a guest who signs up keeps everything — and wrong for anybody
 * else's. Signing out used to leave all of it behind, so the next account to
 * sign in on the device adopted the previous one's XP, profile, Leitner boxes
 * and games, pushed them to its OWN cloud copy (a fresh account has no remote
 * doc, and mergeLearn(local, null) is just `local`), and skipped onboarding
 * because the inherited profile said it had already been done.
 *
 * Two halves, because an invariant enforced only at a call site is a
 * convention:
 *   - signing out wipes the account's local data (auth.js#signOutUser);
 *   - auth.js also refuses to hand a user what ANOTHER real account left here
 *     (`ownedByAnotherAccount`). A session can end without our sign-out —
 *     revoked, expired, deleted on another device — and a wipe can be cut
 *     short, so the sign-out path alone is not a guarantee.
 *
 * An anonymous owner is not "another account": a guest signing up, or signing
 * in to the Google account they already have, is exactly the merge the rest of
 * the app is built around, and refusing it would throw their progress away.
 * A device with no owner on record (everything before this file existed) is
 * claimed as-is — there is no way to know whose it was. */
import { cancelAllReminders } from "./notify.js";
import { emptyState, loadLearn, saveLearn } from "./learn/persist.js";
import { DEVICE_LOCAL_STORAGE_KEYS, LOCAL_STORAGE_KEYS } from "./userDataPaths.js";

const OWNER_KEY = "towardsgm:owner";

/* The Learn blob's settings are this-device-wins in sync.js — a board theme,
 * the language, sound — so they belong to the device and survive a sign-out,
 * the same way they already win over the account's copy on sign-in. These two
 * are the exceptions sync.js also makes: facts about a person ("I have read
 * this explanation"), which the next person has not done. */
const PERSONAL_SETTINGS = ["dismissed", "seenCoursePathNudge"];

export function deviceOwner() {
  try {
    return JSON.parse(localStorage.getItem(OWNER_KEY)) || null;
  } catch {
    return null;
  }
}

/* Called for every signed-in user auth.js emits, and again after a guest links
 * a credential: linking keeps the uid, so onAuthStateChanged does not fire,
 * and an owner still marked anonymous would let the NEXT account merge in. */
export function recordOwner(user) {
  if (!user?.uid) return;
  try {
    localStorage.setItem(OWNER_KEY, JSON.stringify({ uid: user.uid, anonymous: Boolean(user.isAnonymous) }));
  } catch {
    /* storage unavailable — nothing persists for anyone, so nothing can leak */
  }
}

export function ownedByAnotherAccount(user) {
  const owner = deviceOwner();
  return Boolean(user?.uid && owner?.uid && owner.uid !== user.uid && !owner.anonymous);
}

/* Everything account-shaped goes; the device's own choices (analytics consent,
 * the public catalog cache, the Learn settings above) stay. Account deletion
 * has its own, total wipe (accountDeletion.js#wipeLocalData) — leaving the
 * device's choices behind is right for a sign-out and wrong for an erasure.
 *
 * Callers reload afterwards: several modules hold this state in memory too
 * (notify.js's cached prefs, a mounted LearnCenter's copy of the blob), and a
 * reload is the one reset that cannot miss a cache someone adds later. */
export async function wipeAccountData() {
  const { settings } = loadLearn();
  for (const key of LOCAL_STORAGE_KEYS) {
    if (DEVICE_LOCAL_STORAGE_KEYS.includes(key)) continue;
    try {
      localStorage.removeItem(key);
    } catch {
      /* private mode — nothing was stored to begin with */
    }
  }
  const base = emptyState();
  const kept = { ...settings };
  for (const key of PERSONAL_SETTINGS) delete kept[key];
  saveLearn({ ...base, settings: { ...base.settings, ...kept } });
  await cancelAllReminders();
  /* Dynamic: the games cache lives in the Learn/Ledger chunks, and auth.js —
   * which calls this — is in the main one. */
  const { clearAllCachedGames } = await import("./gamesCache.js");
  await clearAllCachedGames();
}
